import React, { useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { AnimatePresence, motion } from 'motion/react'
import { AlertTriangle, ArrowLeft, Sparkles } from "lucide-react"
import heroArt from "../assets/images/honeyguide_hero.png"
import { getPendingSignupInfo, postFinishSignupFromLink } from "../api/client"
import { UserContext } from "../components/UserContext"

const SURFACE_SPRING = { type: 'spring' as const, stiffness: 500, damping: 30 }
const BUTTON_SPRING = { type: 'spring' as const, stiffness: 400, damping: 20 }

const NOISE_SVG =
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'>" +
    "<filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter>" +
    "<rect width='100%' height='100%' filter='url(%23n)' opacity='0.7'/></svg>\")"

const FinishSignupPage = () => {
    const navigate = useNavigate()
    const { refreshUser } = useContext(UserContext)
    const [email, setEmail] = useState<string | null>(null)
    const [pendingError, setPendingError] = useState<string | null>(null)
    const [firstName, setFirstName] = useState("")
    const [lastName, setLastName] = useState("")
    const [error, setError] = useState("")
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        getPendingSignupInfo()
            .then((info) => setEmail(info.email))
            .catch(() => setPendingError(
                "We couldn't find a pending sign-up. Your link may have expired " +
                "— request a new one from the login page."))
    }, [])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!firstName.trim()) {
            setError("Please enter your first name.")
            return
        }
        setError("")
        setIsLoading(true)
        try {
            await postFinishSignupFromLink({
                first_name: firstName.trim(),
                last_name: lastName.trim() || undefined,
            })
            // Signed in on /finish-signup routes to Chats (App.tsx), so this
            // page only needs the user to arrive.
            await refreshUser()
        } catch (err: any) {
            console.error("Finish signup failed:", err)
            setError(err.response?.data?.error || "Could not complete sign-up. Please try again.")
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="relative flex justify-center px-4 py-8 sm:py-14">
            {/* Tinted gradient stage */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 -z-10"
                style={{
                    background:
                        "radial-gradient(55% 45% at 50% 12%, rgba(252,211,77,0.28) 0%, rgba(252,211,77,0) 65%)," +
                        "radial-gradient(60% 50% at 100% 90%, rgba(244,114,182,0.18) 0%, rgba(244,114,182,0) 60%)," +
                        "radial-gradient(60% 50% at 0% 100%, rgba(167,139,250,0.12) 0%, rgba(167,139,250,0) 60%)," +
                        "linear-gradient(180deg, #fdfaf4 0%, #fbf3e8 100%)",
                }}
            />
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 -z-10 opacity-[0.04] mix-blend-multiply"
                style={{ backgroundImage: NOISE_SVG }}
            />

            <div className="w-full max-w-md">
                <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={SURFACE_SPRING}
                    className="relative rounded-3xl border border-stone-200/70 bg-white/95 px-6 py-9 sm:px-9 sm:py-11 shadow-[0_30px_60px_-30px_rgba(120,53,15,0.18),0_8px_24px_-12px_rgba(120,53,15,0.10)] backdrop-blur"
                >
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-amber-300/70 to-transparent"
                    />

                    <div className="flex flex-col items-center text-center">
                        <motion.img
                            src={heroArt}
                            alt=""
                            width="150"
                            height="164"
                            className="mb-1"
                            initial={{ opacity: 0, scale: 0.6 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ ...SURFACE_SPRING, delay: 0.04 }}
                        />
                        <motion.h1
                            className="font-serif text-3xl tracking-tight text-stone-900"
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ ...SURFACE_SPRING, delay: 0.08 }}
                        >
                            {pendingError ? "Honeyguide" : "Finish signing up"}
                        </motion.h1>
                        <motion.div
                            className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-stone-500"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ ...SURFACE_SPRING, delay: 0.14 }}
                        >
                            <span className="h-px w-6 bg-stone-300" />
                            {pendingError ? "Link expired" : "One more step"}
                            <span className="h-px w-6 bg-stone-300" />
                        </motion.div>
                    </div>

                    <motion.div
                        className="mt-7"
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ ...SURFACE_SPRING, delay: 0.20 }}
                    >
                        <AnimatePresence mode="wait" initial={false}>
                            {pendingError && (
                                <motion.div
                                    key="pending-error"
                                    initial={{ opacity: 0, y: -6, scale: 0.97 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -6, scale: 0.97 }}
                                    transition={SURFACE_SPRING}
                                >
                                    <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50/80 px-4 py-3 text-rose-800">
                                        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={2} />
                                        <span className="text-left text-sm">{pendingError}</span>
                                    </div>
                                    <motion.button
                                        type="button"
                                        onClick={() => navigate("/login")}
                                        whileHover={{ scale: 1.015, y: -1 }}
                                        whileTap={{ scale: 0.985 }}
                                        transition={BUTTON_SPRING}
                                        className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-action to-action-dark px-4 py-2.5 text-sm font-medium text-plum shadow-[0_8px_24px_-10px_rgba(180,83,9,0.55)] hover:shadow-[0_10px_28px_-10px_rgba(180,83,9,0.65)]"
                                    >
                                        <ArrowLeft className="h-4 w-4" strokeWidth={2.25} />
                                        Back to login
                                    </motion.button>
                                </motion.div>
                            )}

                            {!pendingError && (
                                <motion.form
                                    key="form"
                                    onSubmit={handleSubmit}
                                    initial={{ opacity: 0, y: -6, scale: 0.97 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -6, scale: 0.97 }}
                                    transition={SURFACE_SPRING}
                                >
                                    {email && (
                                        <div className="mb-4 flex items-center gap-2 rounded-xl bg-amber-50/70 border border-amber-200/70 px-3 py-2 text-left text-sm text-stone-700">
                                            <Sparkles className="h-4 w-4 shrink-0 text-amber-600" strokeWidth={2.25} />
                                            <span>
                                                Creating your account with <span className="font-semibold text-stone-900">{email}</span>
                                            </span>
                                        </div>
                                    )}

                                    <FieldLabel htmlFor="first-name">First name</FieldLabel>
                                    <PrettyInput
                                        id="first-name"
                                        type="text"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        required
                                    />

                                    <div className="mt-4">
                                        <FieldLabel htmlFor="last-name">
                                            Last name <span className="ml-1 text-stone-400 font-normal normal-case tracking-normal">(optional)</span>
                                        </FieldLabel>
                                        <PrettyInput
                                            id="last-name"
                                            type="text"
                                            value={lastName}
                                            onChange={(e) => setLastName(e.target.value)}
                                        />
                                    </div>

                                    {error && <ErrorMessage>{error}</ErrorMessage>}

                                    <SubmitButton loading={isLoading} disabled={!email} loadingLabel="Creating account…">
                                        Create account
                                    </SubmitButton>
                                </motion.form>
                            )}
                        </AnimatePresence>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    )
}

const FieldLabel = ({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) => (
    <label htmlFor={htmlFor} className="mb-1.5 block text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">
        {children}
    </label>
)

const PrettyInput = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
    <input
        {...props}
        className="w-full rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-stone-900 placeholder:text-stone-400 transition focus:border-amber-400 focus:outline-none focus:ring-4 focus:ring-amber-400/20"
    />
)

const ErrorMessage = ({ children }: { children: React.ReactNode }) => (
    <motion.div
        initial={{ opacity: 0, y: -4, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={SURFACE_SPRING}
        role="alert"
        className="mt-3 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
    >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
        <span className="whitespace-pre-line text-left">{children}</span>
    </motion.div>
)

const SubmitButton = ({ loading, disabled, loadingLabel, children }: { loading: boolean; disabled?: boolean; loadingLabel: string; children: React.ReactNode }) => {
    const inactive = loading || disabled
    return (
        <motion.button
            type="submit"
            disabled={inactive}
            whileHover={inactive ? undefined : { scale: 1.015, y: -1 }}
            whileTap={inactive ? undefined : { scale: 0.985 }}
            transition={BUTTON_SPRING}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-action to-action-dark px-4 py-2.5 text-sm font-medium text-plum shadow-[0_8px_24px_-10px_rgba(180,83,9,0.55)] disabled:cursor-not-allowed disabled:opacity-60 hover:shadow-[0_10px_28px_-10px_rgba(180,83,9,0.65)]"
        >
            {loading ? (
                <>
                    <SpinnerSvg />
                    {loadingLabel}
                </>
            ) : children}
        </motion.button>
    )
}

const SpinnerSvg = () => (
    <svg className="-ml-1 h-4 w-4 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
)

export default FinishSignupPage
